import React from 'react';
import { Link } from 'react-router-dom';
import {
  Layers,
  BookOpen,
  Zap,
  ArrowRight,
  SearchCode,
  Tag,
  PenTool,
  Package,
  Globe,
  Workflow,
  FileEdit,
  GitBranch,
} from 'lucide-react';
import {
  PageShell, Section, SectionLabel, Heading,
  Card, Badge,
} from '@/components/elevation';

const workflowSteps = [
  {
    name: 'Gathering',
    description: 'Collecting ideas, content, and insights from diverse sources',
    engine: 'Prospector Engine',
    icon: SearchCode,
    badge: 'default' as const,
    link: '/elevation/operations',
  },
  {
    name: 'Tagging',
    description: 'Adding metadata, categorization, and structure to collected content',
    engine: 'Knowledge Engine',
    badge: 'green' as const,
    icon: Tag,
    link: '/elevation/operations',
  },
  {
    name: 'Formulating',
    description: 'Research, refinement, and development of ideas into coherent frameworks',
    engine: 'Curation Engine · Canon Engine',
    badge: 'amber' as const,
    icon: BookOpen,
    link: '/elevation/operations',
  },
  {
    name: 'Creating',
    description: 'Authoring content and designing visual assets for maximum impact',
    engine: 'Authoring Engine · Studio Engine',
    badge: 'red' as const,
    icon: PenTool,
    link: '/elevation/operations',
  },
  {
    name: 'Applying',
    description: 'Composing content with visuals into coherent, deliverable products',
    engine: 'Assembly Engine',
    badge: 'blue' as const,
    icon: Package,
    link: '/elevation/operations',
  },
  {
    name: 'Publishing',
    description: 'Publishing content across channels to create value and generate revenue',
    engine: 'Publishing Engine',
    badge: 'outline' as const,
    icon: Globe,
    link: '/elevation/operations',
  },
];

const mofLayers = [
  {
    level: 'M3',
    name: 'Metadata',
    description: 'The universal grammar for representing all knowledge. Defines the fields and structure for all models.',
    components: ['Ontology', 'Modality', 'Dynamics', 'Evaluation'],
    icon: Layers,
    path: '/elevation/grammar',
  },
  {
    level: 'M2',
    name: 'Models',
    description: 'Concrete frameworks like DEGAP, Johari Window, Logical Levels. Define structure, relationships, and rules.',
    components: ['Decision Models', 'Assessment Models', 'Process Models'],
    icon: GitBranch,
    path: '/elevation/models',
  },
  {
    level: 'M1',
    name: 'Templates',
    description: 'Ready-to-deploy templates based on M2 models. Convert models into usable forms with specific fields and validation.',
    components: ['Assessment Templates', 'Workshop Templates', 'Planning Templates'],
    icon: FileEdit,
    path: '/elevation/templates',
  },
  {
    level: 'M0',
    name: 'Runtime',
    description: 'Actual experiences and instances. Real workshops, assessments, and interventions with measurable outcomes.',
    components: ['Live Workshops', 'Client Sessions', 'Measured Outcomes'],
    icon: Zap,
    path: '/elevation/runtime',
  },
];

export const DesignOverview: React.FC = () => {
  return (
    <PageShell>
      {/* Hero */}
      <Section>
        <Card className="text-center py-10 px-6">
          <SectionLabel>Overview</SectionLabel>
          <Heading as="h1" size="xl" className="mt-1 mb-3">Design</Heading>
          <p className="text-charcoal-600 dark:text-charcoal-400 max-w-2xl mx-auto leading-relaxed">
            Explore the content workflow and the four-layer knowledge architecture that powers the
            Peak Priorities system.
          </p>
        </Card>
      </Section>

      {/* Content Workflow */}
      <Section>
        <div className="flex items-center gap-2 mb-4">
          <Workflow className="h-4 w-4 text-charcoal-500" />
          <SectionLabel className="mb-0">Content Workflow</SectionLabel>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workflowSteps.map((step) => {
            const StepIcon = step.icon;
            return (
              <Link key={step.name} to={step.link} className="group block">
                <Card className="h-full hover:border-charcoal-300 dark:hover:border-charcoal-600 transition-colors">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-charcoal-100 dark:bg-charcoal-700 flex items-center justify-center shrink-0">
                      <StepIcon className="h-4 w-4 text-charcoal-600 dark:text-charcoal-300" />
                    </div>
                    <div className="min-w-0">
                      <Heading as="h3" size="sm">{step.name}</Heading>
                      <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-0.5 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-charcoal-400 dark:text-charcoal-500">
                      {step.engine}
                    </span>
                    <Badge variant={step.badge}>{step.name}</Badge>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* Four-Layer Architecture */}
      <Section>
        <SectionLabel>Knowledge Architecture</SectionLabel>
        <Heading size="lg" className="mb-1">The Four-Layer Model</Heading>
        <p className="text-sm text-charcoal-500 dark:text-charcoal-400 mb-5">
          M3→M2→M1→M0: from metadata grammar to runtime instances
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {mofLayers.map((layer) => {
            const LayerIcon = layer.icon;
            return (
              <Link key={layer.level} to={layer.path} className="group block">
                <Card className="h-full hover:border-charcoal-300 dark:hover:border-charcoal-600 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 bg-charcoal-100 dark:bg-charcoal-700 flex items-center justify-center shrink-0">
                      <LayerIcon className="h-3.5 w-3.5 text-charcoal-600 dark:text-charcoal-300" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-charcoal-400 dark:text-charcoal-500 block">
                        {layer.level}
                      </span>
                      <Heading as="h3" size="sm">{layer.name}</Heading>
                    </div>
                  </div>
                  <p className="text-xs text-charcoal-500 dark:text-charcoal-400 leading-relaxed mb-3">
                    {layer.description}
                  </p>
                  <div className="space-y-1 mb-3">
                    {layer.components.map((c) => (
                      <div key={c} className="text-[10px] font-mono text-charcoal-400 dark:text-charcoal-500 bg-charcoal-50 dark:bg-charcoal-800 px-2 py-0.5 rounded">
                        {c}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 font-medium">
                    Explore {layer.level}
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </Section>
    </PageShell>
  );
};
